/**
 * The writes behind the mail desk's right-click menus: acting on every
 * conversation in a folded group at once, and marking, moving and deleting a
 * message in one of the mail server's own folders.
 *
 * Its own file rather than more of `mail.ts`, which already holds every read
 * and every single-thread write the desk makes. These share one thing that the
 * rest does not: each call is one press in a context menu, and each answers
 * with enough for the list to redraw itself without reading the folder again.
 */
import { api } from "./api.js";

// ── Conversations, several at once ─────────────────────────────────────────

/** What a whole group can share. Labels and links stay per conversation. */
export interface BulkThreadPatch {
  status?: "open" | "archived" | "spam";
  unread?: boolean;
  starred?: boolean;
}

export const bulkPatchThreads = (ids: string[], patch: BulkThreadPatch) =>
  api.post<{ updated: number }>("/admin/crm/mail/threads/bulk", { ids, ...patch });

export const bulkDeleteThreads = (ids: string[]) =>
  api.post<{ deleted: number }>("/admin/crm/mail/threads/bulk-delete", { ids });

// ── A message in a mail server folder ──────────────────────────────────────

/** Where the mailbox keeps the folders a message goes to with one click. */
export interface ImapTargets {
  inbox: string | null;
  trash: string | null;
  archive: string | null;
  junk: string | null;
}

/**
 * A message as the list drew it. The UIDVALIDITY is required: without it the
 * server cannot tell this message from a different one that took its number
 * after the folder was recreated, and it refuses the write.
 */
export interface ImapMessageRef {
  path: string;
  uid: number;
  uidValidity: string;
  /** The mailbox the folder is in. Absent: the shared one. */
  mailbox?: string;
}

export const setImapMessageFlags = (ref: ImapMessageRef, change: { seen?: boolean; flagged?: boolean }) =>
  api.post<{ ok: true; seen?: boolean; flagged?: boolean }>("/admin/crm/mail/imap/message/flags", { ...ref, ...change });

export interface ImapMoveResult {
  ok: true;
  from: string;
  to: string;
  /** Whether the message had been read - an unread one moves a count with it. */
  seen: boolean;
}

export const moveImapMessage = (ref: ImapMessageRef, to: string) =>
  api.post<ImapMoveResult>("/admin/crm/mail/imap/message/move", { ...ref, to });

export type ImapDeleteResult =
  | { ok: true; trashed: true; to: string; seen: boolean }
  | { ok: true; purged: true; seen: boolean };

/**
 * Delete means Trash. `permanent` is only sent after the operator confirmed a
 * delete that has nowhere to go - inside Trash, or in a mailbox without one.
 */
export const deleteImapMessage = (ref: ImapMessageRef, permanent = false) =>
  api.post<ImapDeleteResult>("/admin/crm/mail/imap/message/delete", { ...ref, permanent });

export const markImapFolderRead = (path: string, mailbox?: string) =>
  api.post<{ ok: true; path: string; marked: number }>("/admin/crm/mail/imap/folders/mark-read", { path, mailbox });
